import { useEffect, useRef } from 'react';
import { getAttributionEventParams } from './attribution.js';

export const GA_MEASUREMENT_ID = 'G-H5NPQSCZ08';

const EVENT_DEDUPE_MS = 1000;
const eventDedupeCache = new Map();
let lastPageviewPath = null;
let lastPageviewLocation = null;
let youtubeApiPromise = null;
let clarityInitialized = false;

const getCurrentPath = () => {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const getPageContext = (pagePath = getCurrentPath()) => {
  if (typeof window === 'undefined') {
    return {
      page_title: '',
      page_path: pagePath,
      page_location: pagePath
    };
  }

  return {
    page_title: document.title,
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`
  };
};

const normalizeParams = (params = {}) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
);

const shouldSend = (dedupeKey, dedupeMs = EVENT_DEDUPE_MS) => {
  const now = Date.now();
  const lastSentAt = eventDedupeCache.get(dedupeKey);

  if (lastSentAt && now - lastSentAt < dedupeMs) {
    return false;
  }

  eventDedupeCache.set(dedupeKey, now);
  return true;
};

export const pageview = (url = getCurrentPath()) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  if (lastPageviewPath === url) return;
  lastPageviewPath = url;

  const pageContext = getPageContext(url);
  const pageReferrer = lastPageviewLocation || document.referrer || undefined;
  lastPageviewLocation = pageContext.page_location;

  window.gtag('event', 'page_view', normalizeParams({
    ...pageContext,
    page_referrer: pageReferrer,
    ...getAttributionEventParams()
  }));
};

export const trackEvent = (eventName, params = {}, options = {}) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  const cleanParams = normalizeParams(params);
  const payload = {
    ...getPageContext(),
    ...getAttributionEventParams(),
    ...cleanParams
  };
  const dedupeKey = options.dedupeKey || `${eventName}:${JSON.stringify(payload)}`;

  if (!shouldSend(dedupeKey, options.dedupeMs)) return;

  window.gtag('event', eventName, payload);
};

export const initializeClarity = () => {
  const clarityId = import.meta.env.VITE_MICROSOFT_CLARITY_ID;
  if (typeof window === 'undefined' || !clarityId || clarityInitialized || window.clarity) return;

  try {
    const consent = JSON.parse(window.localStorage.getItem('cookieConsent') || 'null');
    if (consent?.analytics === false) return;
  } catch {
    return;
  }

  clarityInitialized = true;
  window.clarity = window.clarity || function clarityQueue() {
    (window.clarity.q = window.clarity.q || []).push(arguments);
  };

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${clarityId}`;
  document.head.appendChild(script);
};

export const trackStartFree = (location) => {
  trackEvent('start_free_click', { button_location: location });
};

export const trackSignupFree = (location) => {
  trackEvent('sign_up_free_click', { button_location: location });
};

export const trackSignupComplete = (location) => {
  trackEvent('signup_complete', { button_location: location });
};

export const trackWatchDemo = (location) => {
  trackEvent('watch_demo_click', { button_location: location });
};

export const trackLoginClick = (location) => {
  trackEvent('login_click', { button_location: location });
};

export const trackSignup = () => {
  trackEvent('sign_up', {}, { dedupeKey: `sign_up:${getCurrentPath()}` });
};

export const trackLogin = () => {
  trackEvent('login', {}, { dedupeKey: `login:${getCurrentPath()}` });
};

export const trackVideoStart = (title) => {
  trackEvent('video_start', { video_title: title }, { dedupeKey: `video_start:${title}:${getCurrentPath()}` });
};

export const trackVideoProgress = (title, progress) => {
  trackEvent(
    'video_progress',
    { video_title: title, progress },
    { dedupeKey: `video_progress:${title}:${progress}:${getCurrentPath()}` }
  );
};

export const trackVideoComplete = (title) => {
  trackEvent('video_complete', { video_title: title }, { dedupeKey: `video_complete:${title}:${getCurrentPath()}` });
};

export const trackCTA = (action, buttonText, location) => {
  trackEvent(action, {
    button_text: buttonText,
    button_location: location
  });
};

export const useVideoAnalytics = (title) => {
  const hasStartedRef = useRef(false);
  const progressRef = useRef(new Set());

  const trackStart = () => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    trackVideoStart(title);
  };

  const trackProgress = (event) => {
    const video = event.currentTarget;
    if (!video.duration || Number.isNaN(video.duration)) return;

    const percentWatched = (video.currentTime / video.duration) * 100;

    [25, 50, 75].forEach((milestone) => {
      if (percentWatched >= milestone && !progressRef.current.has(milestone)) {
        progressRef.current.add(milestone);
        trackVideoProgress(title, milestone);
      }
    });
  };

  const trackComplete = () => {
    trackStart();
    trackVideoComplete(title);
  };

  return {
    onPlay: trackStart,
    onTimeUpdate: trackProgress,
    onEnded: trackComplete
  };
};

const loadYouTubeIframeApi = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve) => {
    const previousCallback = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousCallback === 'function') {
        previousCallback();
      }
      resolve(window.YT);
    };

    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return youtubeApiPromise;
};

export const withYouTubeAnalyticsParams = (src) => {
  if (!src) return src;

  try {
    const url = new URL(src);
    url.searchParams.set('enablejsapi', '1');

    if (typeof window !== 'undefined') {
      url.searchParams.set('origin', window.location.origin);
    }

    return url.toString();
  } catch {
    return src;
  }
};

export const withVimeoAnalyticsParams = (src, playerId) => {
  if (!src) return src;

  try {
    const url = new URL(src);
    url.searchParams.set('api', '1');

    if (playerId) {
      url.searchParams.set('player_id', playerId);
    }

    return url.toString();
  } catch {
    return src;
  }
};

export const useVimeoIframeAnalytics = (title, enabled = true) => {
  const iframeRef = useRef(null);
  const progressRef = useRef(new Set());
  const hasStartedRef = useRef(false);
  const playerIdRef = useRef(`vimeo-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!enabled || !iframeRef.current) return undefined;

    const iframe = iframeRef.current;

    const sendCommand = (method, value) => {
      iframe.contentWindow?.postMessage(JSON.stringify({ method, value }), '*');
    };

    const handleMessage = (event) => {
      if (event.source !== iframe.contentWindow || !`${event.origin}`.includes('vimeo.com')) return;

      let payload = event.data;

      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }

      if (payload.event === 'play' && !hasStartedRef.current) {
        hasStartedRef.current = true;
        trackVideoStart(title);
      }

      if (payload.event === 'timeupdate' && typeof payload.data?.percent === 'number') {
        const percentWatched = payload.data.percent * 100;

        [25, 50, 75].forEach((milestone) => {
          if (percentWatched >= milestone && !progressRef.current.has(milestone)) {
            progressRef.current.add(milestone);
            trackVideoProgress(title, milestone);
          }
        });
      }

      if (payload.event === 'ended') {
        trackVideoComplete(title);
      }
    };

    window.addEventListener('message', handleMessage);

    const handleLoad = () => {
      sendCommand('addEventListener', 'play');
      sendCommand('addEventListener', 'timeupdate');
      sendCommand('addEventListener', 'ended');
    };

    iframe.addEventListener('load', handleLoad);
    handleLoad();

    return () => {
      window.removeEventListener('message', handleMessage);
      iframe.removeEventListener('load', handleLoad);
    };
  }, [enabled, title]);

  return {
    iframeRef,
    playerId: playerIdRef.current
  };
};

export const useYouTubeIframeAnalytics = (title, enabled = true) => {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  const hasStartedRef = useRef(false);
  const progressRef = useRef(new Set());

  useEffect(() => {
    if (!enabled || !iframeRef.current) return undefined;

    let isMounted = true;

    const clearProgressInterval = () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const pollProgress = () => {
      const player = playerRef.current;
      if (!player?.getDuration || !player?.getCurrentTime) return;

      const duration = player.getDuration();
      if (!duration) return;

      const percentWatched = (player.getCurrentTime() / duration) * 100;

      [25, 50, 75].forEach((milestone) => {
        if (percentWatched >= milestone && !progressRef.current.has(milestone)) {
          progressRef.current.add(milestone);
          trackVideoProgress(title, milestone);
        }
      });
    };

    loadYouTubeIframeApi().then((YT) => {
      if (!isMounted || !YT?.Player || !iframeRef.current) return;

      playerRef.current = new YT.Player(iframeRef.current, {
        events: {
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.PLAYING) {
              if (!hasStartedRef.current) {
                hasStartedRef.current = true;
                trackVideoStart(title);
              }
              clearProgressInterval();
              intervalRef.current = window.setInterval(pollProgress, 1000);
            }

            if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.BUFFERING) {
              clearProgressInterval();
            }

            if (event.data === YT.PlayerState.ENDED) {
              clearProgressInterval();
              trackVideoComplete(title);
            }
          }
        }
      });
    });

    return () => {
      isMounted = false;
      clearProgressInterval();

      if (playerRef.current?.destroy) {
        playerRef.current.destroy();
      }

      playerRef.current = null;
    };
  }, [enabled, title]);

  return iframeRef;
};

export const event = ({ action, category, label, value }) => {
  trackEvent(action, {
    event_category: category,
    event_label: label,
    value
  });
};
